import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaCode, FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const ContactInfoCard = ({ icon, label, value }) => (
  <div className="flex items-center gap-6 glass p-6 rounded-2xl group hover:border-accent-primary transition-all duration-300">
    <div className="w-12 h-12 flex items-center justify-center bg-accent-primary/10 rounded-xl text-accent-primary text-xl group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div className="flex flex-col">
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span>
      <span className="text-white text-lg font-display font-medium group-hover:text-accent-primary transition-colors">{value}</span>
    </div>
  </div>
);

const Contact = () => {
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);

    // Replace with actual Service ID, Template ID, and Public Key from EmailJS
    // emailjs.sendForm('YOUR_SERVICE_ID', 'YOUR_TEMPLATE_ID', form.current, 'YOUR_PUBLIC_KEY')
    //   .then((result) => {
    //       console.log(result.text);
    //       setStatus({ type: 'success', message: 'Message sent successfully!' });
    //       form.current.reset();
    //   }, (error) => {
    //       console.log(error.text);
    //       setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    //   })
    //   .finally(() => {
    //     setLoading(false);
    //     setTimeout(() => setStatus(null), 5000);
    //   });

    // Dummy success simulation
    setTimeout(() => {
      setLoading(false);
      setStatus({ type: 'success', message: 'Message sent successfully! (Simulation)' });
      form.current.reset();
      setTimeout(() => setStatus(null), 5000);
    }, 1500);
  };

  const socialLinks = [
    { icon: <FaGithub />, link: "https://github.com/yourusername", label: "GitHub" },
    { icon: <FaLinkedin />, link: "https://linkedin.com/in/yourusername", label: "LinkedIn" },
    { icon: <FaTwitter />, link: "https://twitter.com/yourusername", label: "Twitter" },
    { icon: <FaCode />, link: "https://leetcode.com/yourusername", label: "LeetCode" },
  ];

  return (
    <div id="contact" className="w-full section-padding relative">
       <motion.div 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="flex flex-col gap-10"
      >
        <div className="flex items-center gap-4">
          <span className="text-accent-primary font-mono text-xl">05.</span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">Get In Touch</h2>
          <div className="h-[1px] flex-grow bg-slate-800 ml-4 max-w-[300px]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mt-6">
           {/* Left Content */}
           <div className="flex flex-col gap-10">
              <div className="space-y-4">
                 <h3 className="text-3xl font-display font-bold text-white leading-tight">Have a project in mind?<br/><span className="text-accent-primary glow-text">Let's work together!</span></h3>
                 <p className="text-slate-400 text-lg leading-relaxed max-w-[500px]">
                    I am currently looking for new opportunities, my inbox is always open. Whether you have a question or just want to say hi, I’ll try my best to get back to you!
                 </p>
              </div>

              <div className="space-y-4">
                 <ContactInfoCard icon={<FaEnvelope />} label="Email" value="work@yashpatel.live" />
                 <ContactInfoCard icon={<FaPhone />} label="Phone" value="+1 (555) 123-4567" />
                 <ContactInfoCard icon={<FaMapMarkerAlt />} label="Location" value="San Francisco, CA" />
              </div>

              <div className="flex gap-4 pt-4">
                 {socialLinks.map((social, idx) => (
                   <a 
                     key={idx} 
                     href={social.link} 
                     target="_blank" 
                     rel="noreferrer" 
                     className="w-12 h-12 glass rounded-full flex items-center justify-center text-slate-300 text-xl hover:text-accent-primary hover:border-accent-primary transition-all duration-300 group shadow-md"
                     aria-label={social.label}
                   >
                     {social.icon}
                   </a>
                 ))}
              </div>
           </div>

           {/* Right Form */}
           <motion.div 
             initial={{ opacity: 0, x: 50 }}
             whileInView={{ opacity: 1, x: 0 }}
             transition={{ duration: 0.8 }}
             viewport={{ once: true }}
             className="glass p-8 sm:p-10 rounded-2xl glow shadow-2xl relative"
           >
              <form ref={form} onSubmit={sendEmail} className="flex flex-col gap-6">
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                       <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest pl-1">Name</label>
                       <input 
                         required 
                         type="text" 
                         name="user_name" 
                         className="bg-navy/50 border border-slate-800 rounded-lg p-3 text-white outline-none focus:border-accent-primary transition-all" 
                       />
                    </div>
                    <div className="flex flex-col gap-2">
                       <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest pl-1">Email</label>
                       <input 
                         required 
                         type="email" 
                         name="user_email" 
                         className="bg-navy/50 border border-slate-800 rounded-lg p-3 text-white outline-none focus:border-accent-primary transition-all" 
                       />
                    </div>
                 </div>
                 <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest pl-1">Subject</label>
                    <input 
                      required 
                      type="text" 
                      name="subject" 
                      className="bg-navy/50 border border-slate-800 rounded-lg p-3 text-white outline-none focus:border-accent-primary transition-all" 
                    />
                 </div>
                 <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-mono text-slate-500 uppercase tracking-widest pl-1">Message</label>
                    <textarea 
                      required 
                      rows="5" 
                      name="message" 
                      className="bg-navy/50 border border-slate-800 rounded-lg p-3 text-white outline-none focus:border-accent-primary transition-all resize-none" 
                    />
                 </div>
                 
                 <button 
                   disabled={loading}
                   type="submit" 
                   className={`btn-primary flex items-center justify-center gap-3 py-4 mt-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                 >
                    {loading ? 'Sending...' : 'Send Message'}
                    {!loading && <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                 </button>

                 <AnimatePresence>
                   {status && (
                     <motion.div 
                       initial={{ opacity: 0, y: 10 }}
                       animate={{ opacity: 1, y: 0 }}
                       exit={{ opacity: 0 }}
                       className={`mt-4 p-4 rounded-lg text-sm text-center font-mono ${status.type === 'success' ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}
                     >
                       {status.message}
                     </motion.div>
                   )}
                 </AnimatePresence>
              </form>
           </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
